// Utils
import { addMargins } from '../../../common/maths/geometry/rect';
import { getScrollForTarget, getScrollToAlignTargetTopLeft } from '../dom/scrollOffsetCalculations';
import { ViewportOptions } from '../dom/scrollableParentUtils';
import { Rect } from '../../../common/maths/geometry/rect/rectTypes';

type AnimateScrollArgs = {
    scrollableParent: HTMLElement;
    fromScrollLeft: number;
    toScrollLeft: number;
    fromScrollTop: number;
    toScrollTop: number;
    interpolationFactor: number;
};

const activeScrollAnimations = new WeakMap<HTMLElement, () => void>();

/**
 * Animates towards the latest requested position, replacing any scroll on the same container.
 */
export const animateScroll = ({
    scrollableParent,
    fromScrollLeft,
    toScrollLeft,
    fromScrollTop,
    toScrollTop,
    interpolationFactor,

    onAnimationEnd = () => {},
    onAnimationCompleted = () => {},
}: AnimateScrollArgs & { onAnimationEnd?: () => void; onAnimationCompleted?: () => void }) => {
    const cancelPreviousAnimation = activeScrollAnimations.get(scrollableParent);
    cancelPreviousAnimation?.();
    const startScrollLeft = cancelPreviousAnimation ? scrollableParent.scrollLeft : fromScrollLeft;
    const startScrollTop = cancelPreviousAnimation ? scrollableParent.scrollTop : fromScrollTop;

    let animationId: number | null = null;
    let hasEnded = false;

    /**
     * Prevents stale cancellation handles from ending a replacement animation.
     */
    const finish = () => {
        if (hasEnded) return;
        hasEnded = true;
        activeScrollAnimations.delete(scrollableParent);
        scrollableParent.classList.remove('is-scrolling');
        onAnimationEnd();
    };

    const cancel = () => {
        if (hasEnded) return;
        if (animationId !== null) cancelAnimationFrame(animationId);
        finish();
    };

    activeScrollAnimations.set(scrollableParent, cancel);
    scrollableParent.classList.add('is-scrolling');

    const step = (originX: number, destX: number, originY: number, destY: number) => {
        const distX = destX - originX;
        const distY = destY - originY;

        // Terminal case, when both distances are less than a pixel we've reached our last animation frame
        if (Math.abs(distX) < 1 && Math.abs(distY) < 1) {
            scrollableParent.scrollLeft = destX;
            scrollableParent.scrollTop = destY;

            finish();
            onAnimationCompleted();

            return;
        }

        // This will perform a scroll over a duration that is relative to the distance it needs to go.
        // This is better than a constant duration otherwise small distances would move very slowly
        const deltaX = interpolationFactor * distX;
        const deltaY = interpolationFactor * distY;

        const nextX = originX + deltaX;
        const nextY = originY + deltaY;

        // Safari and Chrome round fractional scroll offsets; round consistently for smooth animation.
        scrollableParent.scrollTop = Math.round(nextY);
        scrollableParent.scrollLeft = Math.round(nextX);

        animationId = requestAnimationFrame(() => step(nextX, destX, nextY, destY));
    };

    animationId = requestAnimationFrame(() => step(startScrollLeft, toScrollLeft, startScrollTop, toScrollTop));

    return cancel;
};

/**
 * Settles when the animation completes or is cancelled by a newer scroll.
 */
export const animateScrollPromise = ({
    scrollableParent,
    fromScrollLeft,
    toScrollLeft,
    fromScrollTop,
    toScrollTop,
    interpolationFactor,
}: AnimateScrollArgs) =>
    new Promise<void>((resolve) => {
        animateScroll({
            scrollableParent,
            fromScrollLeft,
            toScrollLeft,
            fromScrollTop,
            toScrollTop,
            interpolationFactor,
            onAnimationEnd: resolve,
        });
    });

type Margins = { top?: number; right?: number; bottom?: number; left?: number };

type ScrollIntoViewArgs = {
    scrollableParent: HTMLElement;
    targetRect: Rect;
    margins?: Margins;
    viewportOptions?: ViewportOptions;
};

/**
 * Retries a direct scroll while a new canvas updates its bounds.
 */
const applyScrollTargetWhileBoundsGrow = ({
    scrollableParent,
    getScrollTarget,
    onTargetReached,
    settlingFrameLimit,
}: {
    scrollableParent: HTMLElement;
    getScrollTarget: () => { scrollLeft: number; scrollTop: number };
    onTargetReached?: () => void;
    settlingFrameLimit: number;
}) => {
    let animationId: number | null = null;
    let unchangedScrollBoundsFrameCount = 0;
    let previousScrollBounds = {
        width: scrollableParent.scrollWidth,
        height: scrollableParent.scrollHeight,
    };

    const attemptScroll = () => {
        const targetScroll = getScrollTarget();
        const scrollTarget = {
            scrollLeft: Math.round(targetScroll.scrollLeft),
            scrollTop: Math.round(targetScroll.scrollTop),
        };

        scrollableParent.scrollLeft = scrollTarget.scrollLeft;
        scrollableParent.scrollTop = scrollTarget.scrollTop;

        // Whole-pixel targets keep browser rounding from masking a clamped scroll.
        const reachedScrollTarget =
            scrollableParent.scrollLeft === scrollTarget.scrollLeft &&
            scrollableParent.scrollTop === scrollTarget.scrollTop;
        if (reachedScrollTarget) {
            onTargetReached?.();
            return;
        }

        const scrollBounds = {
            width: scrollableParent.scrollWidth,
            height: scrollableParent.scrollHeight,
        };
        const scrollBoundsGrew =
            scrollBounds.width > previousScrollBounds.width || scrollBounds.height > previousScrollBounds.height;

        previousScrollBounds = scrollBounds;

        if (scrollBoundsGrew) {
            unchangedScrollBoundsFrameCount = 0;
        } else {
            unchangedScrollBoundsFrameCount += 1;
        }

        if (unchangedScrollBoundsFrameCount <= settlingFrameLimit) {
            animationId = requestAnimationFrame(attemptScroll);
        }
    };

    attemptScroll();

    return () => {
        if (animationId !== null) cancelAnimationFrame(animationId);
    };
};

/**
 * Scrolls a target rectangle into view using an interpolation type animation (like an ease-out),
 * moving the minimal distance needed and not scrolling at all if it's already in view (see
 * getScrollForTarget). The interpolation factor can be provided to speed up or slow down animation.
 *
 * Returns a function that cancels the animation.
 */
const animateScrollIntoView = ({
    scrollableParent,
    targetRect,
    margins = {},
    viewportOptions = {},
    interpolationFactor = 0.3,
}: ScrollIntoViewArgs & { interpolationFactor?: number }) => {
    const scrollTarget = getScrollForTarget({
        scrollableParent,
        targetRect: addMargins(margins, targetRect),
        viewportOptions,
    });

    return animateScroll({
        scrollableParent,
        // Starting point
        fromScrollLeft: scrollableParent.scrollLeft,
        fromScrollTop: scrollableParent.scrollTop,
        toScrollLeft: scrollTarget.scrollLeft,
        toScrollTop: scrollTarget.scrollTop,
        interpolationFactor,
    });
};

/**
 * Instantly jumps a target rectangle into view, aligning its top-left corner with the viewport's
 * top-left corner (see getScrollToAlignTargetTopLeft) - so unlike animateScrollIntoView it always
 * scrolls, even when the target is already visible, and only the top/left margins have any effect.
 *
 * Returns a function that cancels any pending retry (see applyScrollTargetWhileBoundsGrow).
 */
export const snapIntoView = ({
    scrollableParent,
    targetRect,
    margins = {},
    viewportOptions = {},
    onTargetReached,
    settlingFrameLimit = 1,
}: ScrollIntoViewArgs & { onTargetReached?: () => void; settlingFrameLimit?: number }) => {
    const targetRectWithMargins = addMargins(margins, targetRect);

    // Recalculated on each attempt, since a growing canvas changes how far it can be scrolled.
    const getScrollTarget = () =>
        getScrollToAlignTargetTopLeft({
            scrollableParent,
            targetRect: targetRectWithMargins,
            viewportOptions,
        });

    return applyScrollTargetWhileBoundsGrow({
        scrollableParent,
        getScrollTarget,
        onTargetReached,
        settlingFrameLimit,
    });
};

export default animateScrollIntoView;
